import React from 'react';
import './TimesheetHowItWorks.css';

const TimesheetHowItWorks = () => {
  return (
    <>
     <div className="header-bx">
        <span className="logo-pill">OrgGenie</span>
        <h1>Timesheet Module — How It Works</h1>
        <p>Reference guide for all users</p>
      </div>
    <div className='container-fluid   position-relative'>
     

      <div className="page">

        {/* Why Section */}
        <div className="section-label">Why timesheets?</div>
        <div className="why-strip" style={{ marginBottom: '20px' }}>
          <div className="why-item">
            <strong>For you</strong>
            <span>Your logged effort is proof of work — especially on WFH, client-site, or contract days when physical presence isn't visible.</span>
          </div>
          <div className="why-item">
            <strong>For your manager</strong>
            <span>Real visibility into where team capacity is going — which projects are consuming the most time and where people might be blocked.</span>
          </div>
          <div className="why-item">
            <strong>For the organisation</strong>
            <span>The basis for client invoicing, project cost tracking, and profitability. Without logged hours, you cannot accurately bill a client.</span>
          </div>
        </div>

        {/* Process Flow */}
        <div className="section-label">Full process flow</div>
        <div className="flow-wrap">
          <div className="flow-columns">

            {/* Employee Column */}
            <div className="col-emp">
              <div className="flow-col-header">Employee</div>

              <div className="step step-draft">
                <div className="step-title">Log time entry</div>
                <div className="step-sub">Pick a task, set hours. Saved as Draft — only you can see it.</div>
              </div>
              <div className="step-connector">↓</div>

              <div className="step step-draft">
                <div className="step-title">Add more entries</div>
                <div className="step-sub">Log as many tasks as needed across the week. Editable until submitted.</div>
              </div>
              <div className="step-connector">↓</div>

              <div className="step step-submit">
                <div className="step-title">Submit week</div>
                <div className="step-sub">Declare the week complete. Entries lock for editing — manager can now review.</div>
              </div>
              <div className="step-connector">↓</div>

              <div className="step step-sendback" style={{ borderStyle: 'dashed' }}>
                <div className="step-title">If sent back →</div>
                <div className="step-sub">Review the reason, correct entries, re-submit.</div>
              </div>
            </div>

            {/* Arrow 1 */}
            <div className="flow-arrow-col">
              <div className="arrow-h"></div>
            </div>

            {/* Manager Column */}
            <div className="col-mgr">
              <div className="flow-col-header">Manager</div>
              <div className="step step-neutral" style={{ opacity: 0.4, borderStyle: 'dashed' }}>
                <div className="step-title">—</div>
                <div className="step-sub">No action needed until employee submits.</div>
              </div>
              <div className="step-connector" style={{ visibility: 'hidden' }}>↓</div>

              <div className="step step-neutral" style={{ opacity: 0.4, borderStyle: 'dashed' }}>
                <div className="step-title">—</div>
                <div className="step-sub">&nbsp;</div>
              </div>
              <div className="step-connector">↓</div>

              <div className="step step-review">
                <div className="step-title">Notification received</div>
                <div className="step-sub">Sees submitted entries. Reviews by member or by day.</div>
              </div>
              <div className="step-connector">↓</div>

              <div className="branch">
                <strong>Decision</strong>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <div style={{ flex: 1, background: '#EAF3DE', border: '1px solid #C0DD97', borderRadius: '6px', padding: '6px 8px', fontSize: '11px', color: '#173404' }}>
                    <strong style={{ display: 'block', fontWeight: 600, marginBottom: '2px', color: '#3B6D11' }}>✓ Approve</strong>
                    Entries locked permanently. Feed reports and billing.
                  </div>
                  <div style={{ flex: 1, background: '#FCEBEB', border: '1px solid #F7C1C1', borderRadius: '6px', padding: '6px 8px', fontSize: '11px', color: '#501313' }}>
                    <strong style={{ display: 'block', fontWeight: 600, marginBottom: '2px', color: '#A32D2D' }}>↩ Send Back</strong>
                    Must provide a reason. Employee corrects and re-submits.
                  </div>
                </div>
              </div>
            </div>

            {/* Arrow 2 */}
            <div className="flow-arrow-col" style={{ paddingTop: '200px' }}>
              <div className="arrow-h"></div>
            </div>

            {/* Outcome Column */}
            <div className="col-emp">
              <div className="flow-col-header" style={{ color: '#534AB7', borderColor: '#7F77DD' }}>Outcome</div>
              <div className="step step-locked">
                <div className="step-title">Approved hours locked</div>
                <div className="step-sub">Record is final. Audit trail is clean. No one can edit approved entries.</div>
              </div>
              <div className="step-connector">↓</div>

              <div className="step step-approve">
                <div className="step-title">Reports &amp; billing</div>
                <div className="step-sub">Approved hours feed into client invoices, project costs, and utilisation reports.</div>
              </div>

              <div style={{ marginTop: '10px' }}>
                <div className="step step-sendback" style={{ borderStyle: 'dashed' }}>
                  <div className="step-title">Post-approval correction</div>
                  <div className="step-sub">HR Admin only. Entry unlocked with a mandatory audit note — who, when, why.</div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Status Legend */}
        <div className="section-label">Entry status meanings</div>
        <div className="status-row" style={{ marginBottom: '20px' }}>
          <div className="status-card">
            <span className="status-badge s-draft">Draft</span>
            <strong>Work in progress</strong>
            <p>Logged but not submitted. Only you can see it. Fully editable.</p>
          </div>
          <div className="status-card">
            <span className="status-badge s-submit">Submitted</span>
            <strong>Awaiting review</strong>
            <p>Sent to manager. You cannot edit until it is sent back to you.</p>
          </div>
          <div className="status-card">
            <span className="status-badge s-approve">Approved</span>
            <strong>Accepted and locked</strong>
            <p>Manager confirmed. Permanently locked. Feeds into reports and billing.</p>
          </div>
          <div className="status-card">
            <span className="status-badge s-sendback">Sent Back</span>
            <strong>Correction needed</strong>
            <p>Manager returned it with a reason. Edit and re-submit.</p>
          </div>
        </div>

        {/* When can time be logged */}
        <div className="section-label">When can time be logged?</div>
        <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e0ddd8', padding: '16px', marginBottom: '20px' }}>
          <div className="when-grid">
            <div className="when-item when-yes"><strong>✓ Current week</strong>Any day within the current week, at any time.</div>
            <div className="when-item when-yes"><strong>✓ Past weeks</strong>Allowed until the manager has approved — or until the org-level lock window closes.</div>
            <div className="when-item when-no"><strong>✗ Future dates</strong>Not permitted. You cannot log time for work not yet done.</div>
            <div className="when-item when-no"><strong>✗ Leave days</strong>Days marked as leave by HR are shown but cannot be logged against.</div>
            <div className="when-item when-no"><strong>✗ Public holidays</strong>Not loggable by default (on-call exceptions set by HR).</div>
            <div className="when-item when-yes"><strong>✓ After correction</strong>If sent back, you can edit and re-submit entries from any unlocked period.</div>
          </div>
        </div>

        {/* Key Concepts */}
        <div className="section-label">Key concepts</div>
        <div className="concepts-grid">

          <div className="concept-card">
            <h3>What is a task? <span>3 types</span></h3>
            <div className="concept-row">
              <div className="concept-icon ic-assigned">A</div>
              <div className="concept-row-text">
                <strong>Assigned task</strong>
                <span>Created in the Project module and assigned to you. Appears automatically in your picker.</span>
              </div>
            </div>
            <div className="concept-row">
              <div className="concept-icon ic-project">P</div>
              <div className="concept-row-text">
                <strong>Project task (unassigned)</strong>
                <span>Exists in a project you have access to but wasn't assigned to you. Still loggable for ad-hoc contributions.</span>
              </div>
            </div>
            <div className="concept-row">
              <div className="concept-icon ic-otf">+</div>
              <div className="concept-row-text">
                <strong>On-the-fly task</strong>
                <span>Type a task name that doesn't exist yet. It's created inline, attached to a project, and flagged as employee-created. Keeps real work visible.</span>
              </div>
            </div>
          </div>

          <div className="concept-card">
            <h3>Billable vs non-billable</h3>
            <div className="concept-row">
              <div className="concept-icon ic-bill">₹</div>
              <div className="concept-row-text">
                <strong>Billable</strong>
                <span>Hours charged to a client. Set at project/task level by the PM — employees cannot change it. Goes on the invoice.</span>
              </div>
            </div>
            <div className="concept-row">
              <div className="concept-icon ic-nonbill">—</div>
              <div className="concept-row-text">
                <strong>Non-billable</strong>
                <span>Internal cost — pre-sales, internal builds, meetings, training, admin, warranty-period bug fixes. Not invoiced but still tracked for profitability.</span>
              </div>
            </div>
            <div style={{ background: '#f4f2f0', borderRadius: '6px', padding: '8px 10px', marginTop: '8px', fontSize: '11px', color: '#5F5E5A' }}>
              <strong style={{ color: '#1a1a2e' }}>Why does it matter?</strong><br />
              Profitability = (billable hours × billing rate) − (total hours × salary rate). If you're spending 40% of time on non-billable work, the org needs to know.
            </div>
          </div>

        </div>

        {/* FAQ Section - FULL */}
        <div className="section-label">Frequently asked questions</div>
        <div className="faq-wrap">
          <details>
            <summary>Can I edit entries after submitting?</summary>
            <div className="faq-body">
              <p>Not until the manager sends them back to you. Once submitted, entries are locked for editing. If you realise you made a mistake, let your manager know — they can send the week back so you can correct it. After approval, only an HR Admin can unlock an entry, and it creates an audit trail record.</p>
            </div>
          </details>
          <details>
            <summary>What if I forget to submit before the weekly deadline?</summary>
            <div className="faq-body">
              <p>You'll receive a reminder notification before the deadline. If the deadline passes with entries still in Draft, the system may auto-submit them (depending on your org's configuration) or lock the week from further edits. Check with your HR team for your org's specific cutoff policy.</p>
            </div>
          </details>
          <details>
            <summary>My task isn't in the list. What do I do?</summary>
            <div className="faq-body">
              <p>Type the task name in the search/entry field. If it's not found, you can create it on the fly — just make sure to link it to the correct project. The task will be created in that project and flagged as employee-created so the Project Manager can see it. If in doubt about the project, check with your PM before logging.</p>
            </div>
          </details>
          <details>
            <summary>What does "sent back" mean for my salary?</summary>
            <div className="faq-body">
              <p>Being sent back is not a deduction or a penalty. It means your manager needs clarification or correction on the entries — wrong project, unusual hours, or a mismatch with what was delivered. Correct and re-submit. Salary is linked to your overall effort being logged and approved, not to how many times an entry goes back and forth.</p>
            </div>
          </details>
          <details>
            <summary>Can my manager edit my entries?</summary>
            <div className="faq-body">
              <p>No. Managers approve or send back — they never edit on your behalf. This protects both parties: your entries remain yours, and the manager's approval is a genuine review, not a silent correction. If something is wrong, it comes back to you to fix.</p>
            </div>
          </details>
          <details>
            <summary>I worked on a public holiday. Can I log it?</summary>
            <div className="faq-body">
              <p>Not by default. Public holidays are locked like leave days. If your org has on-call or holiday work arrangements, HR can unlock specific dates for specific employees. Speak to your HR team to have the day enabled before logging.</p>
            </div>
          </details>
          <details>
            <summary>I'm a WFH / contract / client-site employee. Does this work the same way?</summary>
            <div className="faq-body">
              <p>Yes — the timesheet module works identically regardless of where you work from. Location of work doesn't change the process. You log tasks, submit, and your manager approves. For contract staff, you can only log against the projects you're contracted to; other projects won't appear in your picker.</p>
            </div>
          </details>
          <details>
            <summary>A task has been open for several days with no update. What happens?</summary>
            <div className="faq-body">
              <p>If a task has logged hours across 3+ consecutive days with no status change in the Project module, it surfaces as a flag for your manager. This isn't a productivity alarm — it's a prompt to check whether you're blocked, whether the task is mis-scoped, or whether you need additional resource. Your manager will reach out to help, not to reprimand.</p>
            </div>
          </details>
          <details>
            <summary>Who can see my timesheet entries?</summary>
            <div className="faq-body">
              <p><strong>Draft entries</strong> — only you.<br />
              <strong>Submitted entries</strong> — you and your approving manager.<br />
              <strong>Approved entries</strong> — you, your manager, and HR admins (for reporting and payroll purposes).<br />
              Project Managers can see hours logged against their projects in aggregate reports, but not your individual daily log.</p>
            </div>
          </details>
        </div>

        <div className="footer">OrgGenie Timesheet · Reference guide · BizBrolly Solutions</div>

      </div>
      </div>
    </>
  );
};

export default TimesheetHowItWorks;